import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo } from "react";
import { Masked } from "work/_shared/masked-fields";
import VendorTeamReadOnly from "work/work-agreements/teams/vendor-team/_views/vendor-team-readonly-view";
import WorkAgreement from "work/work-agreements/work-agreement";

const ListContainer = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(2),
  height: "100%",
  marginLeft: theme.spacing(3),
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
const InfoContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: "row",
  flexWrap: "nowrap",
}));
const TeamRestrictedText = styled(Typography)(({ theme }) => ({
  fontStyle: "italic",
  fontSize: "1.1rem",
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));
const TeamReview = styled(VendorTeamReadOnly)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  marginLeft: theme.spacing(2),
}));

type TeamTabProps = {
  entityId: Guid;
  workAgreement: WorkAgreement;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const { workAgreement } = props;

  const individualUserIds: Set<string> = useMemo(() => {
    if (Masked.isMasked(workAgreement.vendorTeam) || !workAgreement.vendorTeam?.leader) return new Set<string>();

    return new Set([
      workAgreement.vendorTeam?.leader.userId.value,
      ...(workAgreement.vendorTeam?.members.map((member) => member.userId.value) ?? []),
    ]);
  }, [workAgreement.vendorTeam]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualUserIds.size ? { userIds: individualUserIds } : skipToken,
  );

  const individuals = useMemo(() => {
    if (!individualQuery.data) return [];
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const leader = individuals.find(
    (ind: Individual) =>
      Masked.isPresentNotMasked(workAgreement.vendorTeam) && ind.userId.isEqualTo(workAgreement.vendorTeam.leader?.userId),
  );

  const members =
    individuals.filter(
      (ind: Individual) =>
        Masked.isNotMasked(workAgreement.vendorTeam) && !ind.userId.isEqualTo(workAgreement.vendorTeam?.leader?.userId),
    ) ?? [];

  if (Masked.isMasked(workAgreement.vendorTeam) || Masked.isMasked(workAgreement.teamRestricted)) {
    return "You do not have permission to view this team.";
  }

  return (
    <ListContainer container>
      <InfoContainer>
        <Tooltip 
          title="If a team is restricted, re-negotiation is required to change the team members. 
          If not restricted, team members can be changed without re-negotiation."
          placement="bottom-start"
        >
          <InfoIcon color="info" />
        </Tooltip>
        <TeamRestrictedText>
          {workAgreement.teamRestricted.value ? "Team is restricted" : "Team is not restricted"}
        </TeamRestrictedText>
      </InfoContainer>
      <Grid size="grow">
        <TeamReview
          leader={leader ?? null}
          members={members}
          hideCommentButton
        />
      </Grid>
    </ListContainer>
  );
}
